import type { ProAdminDataDeletionQuery, ProAdminRefundListQuery } from "dutyon-server/types";

type QueryScopeId = string | null | undefined;
type QueryDateScope = string | Date | null | undefined;

type PageQuery = {
  page: number;
  limit: number;
};

type NurseListQuery = PageQuery & {
  companyId?: QueryScopeId;
  teamId?: QueryScopeId;
  filter?: string;
};

type DutyRequestListQuery = PageQuery & {
  status?: string;
  teamId?: QueryScopeId;
  from?: string;
  to?: string;
};

const auth = {
  me: ["me"] as const,
  profile: (ownerId: QueryScopeId) => ["me", ownerId] as const,
  authorizedTeams: (ownerId: QueryScopeId, companyId: QueryScopeId, teamId: QueryScopeId) =>
    ["teams", "authorized", ownerId, companyId, teamId] as const,
};

const invitations = {
  mine: ["myInvitation"] as const,
  byCompany: (companyId: QueryScopeId) => ["invitations", "company", companyId] as const,
};

const wantedsRoot = ["wanteds"] as const;
const inquiriesRoot = ["inquiries"] as const;
const teamMembersRoot = ["myTeamMembers"] as const;
const companiesRoot = ["companies"] as const;
const teamsRoot = ["teams"] as const;
const nursesRoot = ["nurses"] as const;
const noticesRoot = ["notices"] as const;
const dutyRequestsRoot = ["dutyRequests"] as const;
const workShiftsRoot = ["workShifts"] as const;
const holidaysRoot = ["holidays"] as const;
const jobPositionsRoot = ["jobPositions"] as const;
const usersRoot = ["users"] as const;
const trialApplicationsRoot = ["trialApplications"] as const;
const dutyResponseCommentsRoot = ["dutyResponseComments"] as const;
const calendarRoot = ["googleCalendar"] as const;
const dutiesRoot = ["duties"] as const;
const dutyDraftsRoot = ["dutyDrafts"] as const;
const dutySharesRoot = ["dutyShares"] as const;
const attendancesRoot = ["attendances"] as const;

/**
 * DutyOn 클라이언트가 공유하는 React Query cache key 계약입니다.
 * 목록 무효화가 필요한 도메인은 `all`을 prefix로 사용합니다.
 */
export const queryKeys = {
  generationNotifications: {
    page: (sessionNonce: string, status: string, cursor?: string) =>
      ["generation-notifications", sessionNonce, status, cursor ?? null] as const,
  },
  kakao: {
    sdk: ["kakao-sdk"] as const,
  },
  // Owner scope prevents cached Pro data from leaking across account switches.
  pro: {
    adminDataDeletionRequests: (ownerId: string, filters: ProAdminDataDeletionQuery = {}) =>
      ["pro-admin", ownerId, "data-deletion-requests", filters] as const,
    adminRefundRequests: (ownerId: string, filters: ProAdminRefundListQuery = {}) =>
      ["pro-admin", ownerId, "refund-requests", filters] as const,
    adminRefundRequest: (ownerId: string, id: string) =>
      ["pro-admin", ownerId, "refund-request", id] as const,
    publicPrices: () => ["pro-public", "prices"] as const,
    all: (ownerId: string) => ["pro", ownerId] as const,
    me: (ownerId: string) => ["pro", ownerId, "me"] as const,
    nurses: (ownerId: string) => ["pro", ownerId, "nurses"] as const,
    positions: (ownerId: string) => ["pro", ownerId, "positions"] as const,
    workShifts: (ownerId: string) => ["pro", ownerId, "work-shifts"] as const,
    schedules: (
      ownerId: string,
      filters: {
        month?: string;
        year?: number;
        status?: string;
        q?: string;
        cursor?: string;
        sort?: string;
        limit?: number;
      } = {},
    ) => ["pro", ownerId, "schedules", filters] as const,
    schedule: (ownerId: string, scheduleId: string) =>
      ["pro", ownerId, "schedule", scheduleId] as const,
    version: (ownerId: string, scheduleId: string, versionId: string) =>
      ["pro", ownerId, "schedule", scheduleId, "versions", versionId] as const,
    generation: (ownerId: string, jobId: string) => ["pro", ownerId, "generation", jobId] as const,
    generationRequest: (ownerId: string, requestKey: string) =>
      ["pro", ownerId, "generation-request", requestKey] as const,
    quota: (ownerId: string, month: string) => ["pro", ownerId, "quota", month] as const,
    holidays: (ownerId: string, month: string) => ["pro", ownerId, "holidays", month] as const,
    inquiry: (ownerId: string, id: string) => ["pro", ownerId, "inquiry", id] as const,
    inquiries: (ownerId: string) => ["pro", ownerId, "inquiries"] as const,
    paymentHistory: (ownerId: string) => ["pro", ownerId, "payment-history"] as const,
    refundRequests: (ownerId: string, paymentId: string) =>
      ["pro", ownerId, "refund-requests", paymentId] as const,
    order: (ownerId: string, orderId: string) => ["pro", ownerId, "order", orderId] as const,
    pendingOrder: (ownerId: string) => ["pro", ownerId, "pending-order"] as const,
    billing: (ownerId: string) => ["pro", ownerId, "billing"] as const,
  },
  auth,
  calendar: {
    status: [...calendarRoot, "status"] as const,
  },
  invitations,
  companies: {
    all: companiesRoot,
    detail: (companyId: QueryScopeId) => [...companiesRoot, "detail", companyId] as const,
    admins: (companyId: QueryScopeId) => [...companiesRoot, companyId, "admins"] as const,
    adminSearch: (companyId: QueryScopeId, email: string) =>
      [...companiesRoot, companyId, "adminSearch", email] as const,
    onboardingSearch: (searchCode: string) => [...companiesRoot, "onboarding", searchCode] as const,
  },
  teams: {
    all: teamsRoot,
    list: (page: number, limit: number) => [...teamsRoot, "list", page, limit] as const,
    detail: (teamId: QueryScopeId) => [...teamsRoot, "detail", teamId] as const,
    nurses: (teamId: QueryScopeId) => [...teamsRoot, teamId, "nurses"] as const,
  },
  nurses: {
    all: nursesRoot,
    list: (query: NurseListQuery) => [...nursesRoot, "list", query] as const,
    managedTeam: (teamId: QueryScopeId) => [...nursesRoot, "managedTeam", teamId] as const,
    detail: (nurseId: QueryScopeId) => [...nursesRoot, "detail", nurseId] as const,
  },
  notices: {
    all: noticesRoot,
    list: (target?: string, companyId?: QueryScopeId) =>
      [...noticesRoot, "list", target, companyId] as const,
    detail: (noticeId: QueryScopeId) => [...noticesRoot, "detail", noticeId] as const,
    active: (companyId: QueryScopeId) => ["activeNotice", companyId] as const,
  },
  attendances: {
    all: attendancesRoot,
    management: (query: {
      teamId?: QueryScopeId;
      nurseId?: QueryScopeId;
      from: string;
      to: string;
      page: number;
      limit: number;
    }) => [...attendancesRoot, "management", query] as const,
    today: (nurseId: QueryScopeId) => ["myToday", nurseId] as const,
    month: (nurseId: QueryScopeId) => ["myMonth", nurseId] as const,
    statistics: (nurseId: QueryScopeId, from: string, to: string) =>
      ["workStatistics", nurseId, from, to] as const,
    teamStatus: (teamId: QueryScopeId) => ["teamStatus", teamId] as const,
  },
  duties: {
    all: dutiesRoot,
    byTeamRange: (teamId: QueryScopeId, from: string, to: string) =>
      [...dutiesRoot, "team", teamId, from, to] as const,
    mine: (nurseId: QueryScopeId, from: string, to?: string) =>
      [...dutiesRoot, "mine", nurseId, from, ...(to ? [to] : [])] as const,
    changes: (nurseId: QueryScopeId, from: string, to: string) =>
      [...dutiesRoot, "changes", nurseId, from, to] as const,
  },
  dutyDrafts: {
    all: dutyDraftsRoot,
    list: (teamId: QueryScopeId) => [...dutyDraftsRoot, "list", teamId] as const,
    summary: (teamId: QueryScopeId) => [...dutyDraftsRoot, "summary", teamId] as const,
  },
  dutyShares: {
    all: dutySharesRoot,
    status: (teamId: QueryScopeId, startDate: string, endDate: string) =>
      [...dutySharesRoot, "status", teamId, startDate, endDate] as const,
    public: (slug: QueryScopeId) => [...dutySharesRoot, "public", slug] as const,
  },
  holidays: {
    all: holidaysRoot,
    list: (companyId?: QueryScopeId, view?: string, filters?: unknown) =>
      [...holidaysRoot, "list", companyId, view, filters] as const,
    range: (from: string, to: string) => [...holidaysRoot, "range", from, to] as const,
    companyRange: (companyId: QueryScopeId, from: string, to: string) =>
      [...holidaysRoot, "companyRange", companyId, from, to] as const,
  },
  nurseStatusPeriods: {
    byNurse: (nurseId: QueryScopeId) => ["nurseStatusPeriods", nurseId] as const,
  },
  wanteds: {
    all: wantedsRoot,
    management: (teamId: QueryScopeId, from: QueryDateScope, to: QueryDateScope) =>
      [...wantedsRoot, "management", teamId, from, to] as const,
    password: (companyId: QueryScopeId) => [...wantedsRoot, "password", companyId] as const,
    history: (nurseId: QueryScopeId, from: string, to: string) =>
      [...wantedsRoot, "my", nurseId, "history", from, to] as const,
    statistics: (nurseId: QueryScopeId, year: number) =>
      [...wantedsRoot, "my", nurseId, year] as const,
  },
  workShifts: {
    all: workShiftsRoot,
    byTeam: (teamId: QueryScopeId) => [...workShiftsRoot, "team", teamId] as const,
    detail: (workShiftId: QueryScopeId) => [...workShiftsRoot, "detail", workShiftId] as const,
  },
  jobPositions: {
    all: jobPositionsRoot,
    byCompany: (companyId: QueryScopeId) => [...jobPositionsRoot, "company", companyId] as const,
  },
  teamMembers: {
    all: (teamId: QueryScopeId) => [...teamMembersRoot, teamId] as const,
    detail: (teamId: QueryScopeId, memberId: QueryScopeId) =>
      [...teamMembersRoot, teamId, "detail", memberId] as const,
  },
  inquiries: {
    all: inquiriesRoot,
    list: (status: string) => [...inquiriesRoot, status] as const,
    adminList: (
      page: number,
      limit: number,
      status?: string,
      product?: string,
      category?: string,
    ) => [...inquiriesRoot, "adminList", page, limit, status, product, category] as const,
    detail: (inquiryId: QueryScopeId) => [...inquiriesRoot, "detail", inquiryId] as const,
  },
  dutyRequests: {
    all: dutyRequestsRoot,
    list: (query: DutyRequestListQuery) => [...dutyRequestsRoot, "list", query] as const,
    detail: (requestId: QueryScopeId) => [...dutyRequestsRoot, "detail", requestId] as const,
    mine: ["myDutyRequests"] as const,
  },
  dutyResponseComments: {
    all: dutyResponseCommentsRoot,
    byResponse: (responseId: QueryScopeId) =>
      [...dutyResponseCommentsRoot, "response", responseId] as const,
  },
  trialApplications: {
    all: trialApplicationsRoot,
    list: (page: number, limit: number) => [...trialApplicationsRoot, "list", page, limit] as const,
    detail: (applicationId: QueryScopeId) =>
      [...trialApplicationsRoot, "detail", applicationId] as const,
  },
  users: {
    all: usersRoot,
    list: (query: PageQuery & { filter?: unknown }) => [...usersRoot, "list", query] as const,
    detail: (userId: QueryScopeId) => [...usersRoot, "detail", userId] as const,
  },
  dashboard: {
    statistics: ["dashboard", "statistics"] as const,
  },
  gpuHost: {
    operations: (owner: string) => ["gpu-host", "operations", owner] as const,
  },
  hiracare: {
    reports: (userId: QueryScopeId, companyId: QueryScopeId) =>
      ["hiracare", "reports", userId, companyId] as const,
  },
  settings: {
    mine: ["mySettings"] as const,
  },
  pushDevices: {
    mine: ["myPushDevice"] as const,
  },
  publicWanted: {
    company: (companyId: QueryScopeId) => ["public-wanted-company", companyId] as const,
  },
  terms: {
    all: ["terms"] as const,
    adminList: (page?: number, limit?: number, categoryKey?: string) =>
      ["terms", "adminList", page, limit, categoryKey] as const,
    detail: (id: string) => ["terms", "detail", id] as const,
    latest: () => ["terms", "latest"] as const,
    categories: () => ["terms", "categories"] as const,
  },
  statistics: {
    dutyGeneration: (query?: { startDate?: string; endDate?: string; companyId?: string }) =>
      ["statistics", "dutyGeneration", query] as const,
    dutyRequests: (query?: { startDate?: string; endDate?: string; companyId?: string }) =>
      ["statistics", "dutyRequests", query] as const,
    userTrends: (query?: { startDate?: string; endDate?: string; companyId?: string }) =>
      ["statistics", "userTrends", query] as const,
  },
} as const;
